import React, { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { PropsOf } from '@headlessui/react/dist/types';
import Input from '../misc/Input';
import { formatMoney } from '../../../utils/bankAccountUtil';

type CurrencyInputProps = Omit<
  PropsOf<typeof Input> & {
    handleChange: (text: number) => void;
    debounced?: boolean;
  },
  'onChange'
>;

export default function CurrencyInput({
  defaultValue = '',
  handleChange = () => {},
  autoFocus = false,
  debounced = false,
  ...props
}: CurrencyInputProps) {
  const [rawValue, setRawValue] = useState<string>(props.value || defaultValue?.toString() || '');
  const { i18n } = useTranslation();

  useEffect(() => {
    if (props.value !== undefined) {
      const formatted = formatCurrencyLight(props.value);
      if (formatted !== rawValue) {
        setRawValue(formatted);
      }
    }
  }, [props.value]);

  function getDecimalSeparator(locale: string): string {
    const numberWithDecimal = 1.1;
    return (
      Intl.NumberFormat(locale)
        .formatToParts(numberWithDecimal)
        .find((part) => part.type === 'decimal')?.value || '.'
    );
  }

  const formatCurrency = (input: string): string => {
    const locale = i18n.language;
    const decimalSeparator = getDecimalSeparator(locale);
    const otherSeparator = decimalSeparator === ',' ? '.' : ',';

    const cleaned = input.replace(/[^\d.,]/g, '').replace(otherSeparator, decimalSeparator);
    const parts = cleaned.split(decimalSeparator);
    const euros = parts[0].replace(/^0+(?!$)/, '') || '0';
    let cents = parts[1] || '';

    if (cents.length === 0) {
      cents = '00';
    } else if (cents.length === 1) {
      cents += '0';
    } else if (cents.length > 2) {
      cents = cents.slice(0, 2);
    }

    return `€ ${euros}${decimalSeparator}${cents}`;
  };

  const formatCurrencyLight = (input: string): string => {
    const locale = i18n.language;
    const decimalSeparator = getDecimalSeparator(locale);
    const otherSeparator = decimalSeparator === ',' ? '.' : ',';

    const cleaned = input
      .replace(/[^\d.,]/g, '') // remove anything not digit or separator
      .replace(new RegExp(`\\${otherSeparator}`, 'g'), decimalSeparator); // unify to one separator

    if (cleaned === '') return '';

    // Case: ends with decimal separator (user typed e.g. "5,")
    if (cleaned.endsWith(decimalSeparator)) {
      const numeric = cleaned.slice(0, -1).replace(/^0+(?!$)/, '') || '0';
      return `€ ${numeric}${decimalSeparator}`;
    }

    const [wholeRaw, decimalRaw = ''] = cleaned.split(decimalSeparator);
    const whole = wholeRaw.replace(/^0+(?!$)/, '') || '0';
    const decimal = decimalRaw.slice(0, 2);

    return decimal ? `€ ${whole}${decimalSeparator}${decimal}` : `€ ${whole}`;
  };

  const parseCurrencyToFloat = (text: string): number => {
    const numberString = text.replace(/[€\s]/g, '').replace(',', '.');
    const parsed = parseFloat(numberString);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // Debounced handleChange function using useMemo so it stays stable
  const debouncedHandleChange = useMemo(() => {
    return debounce((value: number) => {
      handleChange?.(value);
    }, 1000);
  }, [handleChange]);

  const handleChangeText = (text: string) => {
    const formatted = formatCurrency(text);
    setRawValue(formatted);
    const parsed = parseCurrencyToFloat(formatted);

    if (debounced) {
      debouncedHandleChange(parsed);
    } else {
      handleChange?.(parsed);
    }
  };

  return (
    <Input
      {...props}
      value={rawValue}
      onChange={(ev) => {
        const formatted = formatCurrencyLight(ev.target.value);
        setRawValue(formatted);

        if (debounced) {
          const parsed = parseCurrencyToFloat(formatted);
          debouncedHandleChange(parsed);
        }
      }}
      onBlur={(e) => {
        if (!debounced) {
          const text = e.target.value;
          handleChangeText(text);
        }
      }}
      placeholder={formatMoney(0, i18n.language, 'EUR')}
      autoFocus={autoFocus}
    />
  );
}
